import { PDF_EXPORT_PAGE_CHUNK_SIZE } from '@extendedWarranty/constants';
import { UploadConfirmationPDF } from '@extendedWarranty/routes/dashboard/ActivationConfirmation';
import { InstallationData, ISerialSubmission } from '@extendedWarranty/types/extendedWarranty';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns/format';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useCallback, useMemo, useRef, useState } from 'react';

const ExportCard = ({ name, date_installed, serial_numbers }: InstallationData & { name: string }) => {
    const pdfContainerRef = useRef<HTMLDivElement | null>(null);
    const [exporting, setExporting] = useState(false);

    const snChunks = useMemo(() => {
        const chunks: ISerialSubmission[][] = [];
        for (let i = 0; i < serial_numbers.length; i += PDF_EXPORT_PAGE_CHUNK_SIZE) {
            /* The slice is calculated arbitrarily, there is a number of serial numbers that fit in each page
                in the first page that number is smaller (i = 0), in subsequent pages it is the PDF_EXPORT_PAGE_CHUNK_SIZE */
            chunks.push(serial_numbers.slice(i, i + PDF_EXPORT_PAGE_CHUNK_SIZE - (i == 0 ? 3 : 0)));
        }

        return chunks;
    }, [serial_numbers]);

    const exportPDF = useCallback(async () => {
        if (!pdfContainerRef.current) return;
        const pages = Array.from(pdfContainerRef.current.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement
        );
        const pdf = new jsPDF('p', 'mm', 'a4');

        // Get PDF dimensions
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (const [i, page] of pages.entries()) {
            const canvas = await html2canvas(page, { scale: 2, useCORS: true, logging: false });
            const imgData = canvas.toDataURL('image/jpeg', 0.8);

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const aspectRatio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const finalWidth = imgWidth * aspectRatio;
            const finalHeight = imgHeight * aspectRatio;

            // Center the image in the PDF
            const xPos = (pdfWidth - finalWidth) / 2;
            const yPos = 10;

            // Skip this action in the first page
            if (i) pdf.addPage();

            pdf.addImage(imgData, 'PNG', xPos, yPos, finalWidth, finalHeight);
        }

        pdf.save(`${name.toLocaleLowerCase().replace(/ /, '-')}-overview.pdf`);
    }, [pdfContainerRef, name]);

    const handleExport = useCallback(async () => {
        setExporting(true);
        setTimeout(async () => {
            await exportPDF();
            setExporting(false);
        }, 1);
    }, [exportPDF]);

    return (
        <>
            <Card className="pt-5 pb-7 px-3 flex flex-col gap-11 md:py-11 md:px-7 md:flex-row md:items-center">
                <Box sx={{ color: (theme) => theme.palette['neutral-900'].main }}>
                    <Typography className="text-xl leading-6" variant="h6" fontWeight={700}>
                        {name}
                    </Typography>
                    <Typography className="sm:text-sm md:leading-4" variant="body1" fontWeight={700}>
                        Installation date
                    </Typography>
                    <Typography className="sm:text-sm md:leading-4" variant="body1">
                        {format(date_installed, 'yyyy-MM-dd')}
                    </Typography>
                </Box>
                <div className="md:ml-auto">
                    <Button
                        fullWidth
                        color="neutral-800"
                        variant="outlined"
                        className="rounded-full"
                        size="large"
                        sx={{ borderColor: (theme) => theme.palette['neutral-700'].main }}
                        onClick={() => handleExport()}
                    >
                        {exporting ? (
                            <CircularProgress size={24} color="neutral-800" />
                        ) : (
                            <Typography>Export PDF</Typography>
                        )}
                    </Button>
                </div>
            </Card>

            {exporting && (
                <div
                    ref={pdfContainerRef}
                    className="absolute min-w-[1330px] max-w-[1330px] w-[1330px] -top-[9999px] -left-[9999px]"
                >
                    {snChunks.map((serial_numbers, index) => (
                        <UploadConfirmationPDF {...{ serial_numbers, name, date_installed, index, primary: !index }} />
                    ))}
                </div>
            )}
        </>
    );
};

export { ExportCard };
